"use client";

import Link from "next/link";
import { useEffect, useMemo, useState, useRef, Suspense } from "react";
import useUtility from "@/app/_hooks/useUtility";
import { useSection } from "./_hooks/useSection";
import BannerSkeleton from "./_skeletons/BannerSkeleton";
import Image from "next/image";
import { frontendImage } from "@/lib/helpers";
import { Convert } from "./Convert";
import ConvertSkeleton from "./_skeletons/ConvertSkeleton";
import { useConvertContext } from "./ConvertContext";
import bannerFlag from "./bannerFlag";
import { motion, AnimatePresence } from "framer-motion";

export const Banner = () => {
  const { trans } = useUtility();
  const { content, elements, loading } = useSection("banner");

  const words = useMemo(() => {
    return elements?.length
      ? elements
          .filter((el) => el?.changing_word)
          .map((el) => `${el.changing_word}.`)
      : [];
  }, [elements]);

  const [currentText, setCurrentText] = useState("");
  const [wordIndex, setWordIndex] = useState(0);
  const [index, setIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);

  const { toSelected } = useConvertContext();

  // Fixed: Add null checking for toSelected
  let countryImages = null;
  if (toSelected?.code) {
    countryImages = bannerFlag.find(
      (flag) =>
        flag.code && flag.code.toUpperCase() === toSelected.code.toUpperCase(),
    );
  }

  const playStoreLink = content?.play_store_link || "#";
  const appStoreLink = content?.app_store_link || "#";

  // Improved image transition state
  const [currentImage, setCurrentImage] = useState(
    countryImages?.countryIcon ||
      "https://res.cloudinary.com/dpkn1ppzj/image/upload/v1754986447/image_98_iagzlf.png",
  );
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [imageKey, setImageKey] = useState(0);

  // Handle image transitions
  useEffect(() => {
    const newImage =
      countryImages?.countryIcon ||
      "https://res.cloudinary.com/dpkn1ppzj/image/upload/v1754986447/image_98_iagzlf.png";

    if (newImage !== currentImage) {
      setIsTransitioning(true);

      // Start transition after a brief delay to ensure state is set
      const transitionTimeout = setTimeout(() => {
        setCurrentImage(newImage);
        setImageKey((prev) => prev + 1);

        // End transition after animation completes
        const endTransitionTimeout = setTimeout(() => {
          setIsTransitioning(false);
        }, 600); // Match CSS transition duration

        return () => clearTimeout(endTransitionTimeout);
      }, 50);

      return () => clearTimeout(transitionTimeout);
    }
  }, [countryImages?.countryIcon, currentImage]);

  // Typewriter effect
  useEffect(() => {
    if (words.length === 0) return;

    const typeEffect = () => {
      const currentWord = words[wordIndex] || "";

      if (!isDeleting) {
        setCurrentText(currentWord.substring(0, index + 1));
        setIndex((prev) => prev + 1);

        if (index + 1 === currentWord.length) {
          setTimeout(() => setIsDeleting(true), 1000);
        }
      } else {
        setCurrentText(currentWord.substring(0, index - 1));
        setIndex((prev) => prev - 1);

        if (index - 1 === 0) {
          setIsDeleting(false);
          setWordIndex((prev) => (prev + 1) % words.length);
        }
      }
    };

    const timer = setTimeout(typeEffect, isDeleting ? 100 : 150);
    return () => clearTimeout(timer);
  }, [index, isDeleting, wordIndex, words]);

  if (loading) {
    return <BannerSkeleton />;
  }

  return (
    <section className="banner-section" style={{ overflow: "hidden" }}>
      <div className="container">
        <div
          className=" gy-4"
          style={{
            position: "relative",
            zIndex: "10 !important",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <motion.div
            className="col-lg-5"
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="banner-content">
              <span className="banner-content__subtitle d-lg-inline-block d-none">
                Africa's premier financial institution
              </span>
              <motion.h1
                className="banner-content__title gradient-text"
                style={{ fontSize: "64px" }}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.3 }}
              >
                Building the Future <br /> of
                <br className="banner-content__title-br" />{" "}
                <span id="changing-text">{currentText}</span>
              </motion.h1>
              <motion.p
                className="banner-content__desc"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 1.2 }}
                style={{ width: "100%" }}
              >
                <span>
                  Belyfted simplifies payments, currency conversion, and
                  transaction management for everyone.
                </span>

                <span className="banner-content__descIcon">
                  <svg
                    width="187"
                    height="102"
                    viewBox="0 0 187 102"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fill-rule="evenodd"
                      clip-rule="evenodd"
                      d="M184.339 84.6794C183.664 84.5167 181.878 84.1559 180.331 83.9224C176.852 83.397 171.617 81.2988 166.849 78.3907C163.193 76.2114 161.506 75.8045 160.774 76.9259C159.79 78.4048 164.369 82.4269 169.998 85.1113C171.699 85.9309 173.112 86.6461 173.181 86.7934C173.48 87.4317 164.143 86.4175 159.496 85.3014C150.189 83.0751 140.527 78.029 131.639 70.8164C125.959 66.2325 121.231 61.5092 111.324 50.7018C107.242 46.2073 101.78 40.8103 99.2377 38.7079C91.7761 32.6232 87.9406 30.9549 75.8587 28.4122C74.6529 28.1389 74.4657 27.8673 73.6915 24.9367C72.6473 21.1745 69.2667 14.9816 66.6942 12.175C57.3553 1.82784 41.3536 -2.05188 22.8345 1.47537C13.4982 3.27494 3.38206 7.35599 1.27349 10.1403C0.920526 10.6649 0.826782 11.4873 1.05683 11.9783C1.47093 12.8621 1.86066 12.7993 5.64139 11.0279C7.90001 9.96965 12.5261 8.34108 15.8663 7.43473C37.8702 1.37657 55.8008 4.64444 65.1517 16.4231C67.2654 19.1452 71.1106 27.0964 70.7453 28.1059C70.6931 28.2501 69.3997 28.5568 67.8669 28.736C65.8232 28.975 63.2041 29.9028 58.3984 32.1544C52.6536 34.846 51.446 35.5914 49.5064 37.6978C44.3227 43.1205 42.3394 48.8401 43.9651 53.46C45.1888 56.8387 46.2028 57.9804 49.0578 59.2175C53.2077 61.0455 55.7592 60.868 61.1541 58.3403C65.5731 56.2698 65.969 55.9646 68.7251 52.5176C72.158 48.2146 74.6416 41.9014 74.9998 36.5315C75.0984 34.5692 75.3611 32.8294 75.5083 32.7604C76.3431 32.3693 84.8962 34.6492 87.7389 36.1316C94.6394 39.4852 98.3596 42.8244 111.219 56.7392C127.481 74.3216 136.848 81.4221 150.954 86.6089C157.611 89.0587 163.038 90.2886 169.204 90.6853L172.208 90.8347L168.932 92.7887C165.041 95.091 162.215 98.3907 162.755 100.054C163.223 101.691 164.713 101.292 168.498 98.381C170.327 97.045 174.811 94.3455 178.516 92.4297C182.228 90.511 185.553 88.5339 185.929 88.0584C186.256 87.6058 186.327 86.7344 186.028 86.0961C185.729 85.4578 184.968 84.8562 184.342 84.6705L184.339 84.6794ZM71.1669 36.3065C70.8363 40.7128 68.2837 46.6392 65.2823 50.3134C60.6263 55.9678 53.6942 57.8985 49.5031 54.8323C47.9553 53.7012 47.8633 53.5048 47.6857 50.9533C47.3413 46.3842 50.0961 42.0397 55.9669 37.9717C57.9433 36.5667 60.6991 35.036 62.1659 34.5882C65.3712 33.5056 71.0988 32.4388 71.3058 32.8807C71.3748 33.028 71.3346 34.6036 71.1702 36.2974L71.1669 36.3065Z"
                      fill="#F39C12"
                    />
                  </svg>
                </span>
              </motion.p>

              <p className="banner-content__status">
                <span className="banner-content__status-icon">
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M6.4004 2.18233C7.36521 2.10489 8.28109 1.72541 9.01795 1.09781C9.84996 0.389183 10.9071 0 12 0C13.0929 0 14.15 0.389183 14.9821 1.09781C15.7189 1.72541 16.6348 2.10489 17.5996 2.18233C18.6893 2.26941 19.7123 2.74178 20.4853 3.51475C21.2582 4.28772 21.7306 5.31074 21.8177 6.4004C21.8942 7.36492 22.2737 8.28144 22.9022 9.01795C23.6108 9.84996 24 10.9071 24 12C24 13.0929 23.6108 14.15 22.9022 14.9821C22.2746 15.7189 21.8951 16.6348 21.8177 17.5996C21.7306 18.6893 21.2582 19.7123 20.4853 20.4853C19.7123 21.2582 18.6893 21.7306 17.5996 21.8177C16.6348 21.8951 15.7189 22.2746 14.9821 22.9022C14.15 23.6108 13.0929 24 12 24C10.9071 24 9.84996 23.6108 9.01795 22.9022C8.28109 22.2746 7.36521 21.8951 6.4004 21.8177C5.31074 21.7306 4.28772 21.2582 3.51475 20.4853C2.74178 19.7123 2.26941 18.6893 2.18233 17.5996C2.10489 16.6348 1.72541 15.7189 1.09781 14.9821C0.389183 14.15 0 13.0929 0 12C0 10.9071 0.389183 9.84996 1.09781 9.01795C1.72541 8.28109 2.10489 7.36521 2.18233 6.4004C2.26941 5.31074 2.74178 4.28772 3.51475 3.51475C4.28772 2.74178 5.31074 2.26941 6.4004 2.18233ZM17.5606 10.0605C17.8338 9.77756 17.985 9.39865 17.9816 9.00535C17.9782 8.61204 17.8204 8.23582 17.5423 7.9577C17.2642 7.67958 16.888 7.52183 16.4947 7.51841C16.1014 7.51499 15.7225 7.66619 15.4396 7.93943L10.5 12.879L8.56044 10.9395C8.27753 10.6662 7.89862 10.515 7.50532 10.5185C7.11202 10.5219 6.73579 10.6796 6.45768 10.9578C6.17956 11.2359 6.0218 11.6121 6.01838 12.0054C6.01497 12.3987 6.16616 12.7776 6.4394 13.0605L9.43946 16.0606C9.72075 16.3418 10.1022 16.4998 10.5 16.4998C10.8977 16.4998 11.2792 16.3418 11.5605 16.0606L17.5606 10.0605Z"
                      fill="#004D00"
                    />
                  </svg>
                </span>
                <span className="banner-content__status-text">
                  {trans(content?.fca)}
                </span>
              </p>

              <motion.div
                className="banner-content__buttons"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.6 }}
              >
                <Link
                  href={playStoreLink}
                  className="banner-content__buttons-item"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => {
                    if (playStoreLink === "#") {
                      e.preventDefault();
                    }
                  }}
                >
                  <Image
                    src="https://res.cloudinary.com/dpkn1ppzj/image/upload/v1752696837/Group_239851_ebj9en.png"
                    alt="Play Store"
                    width={142}
                    height={42}
                  />
                </Link>
                <Link
                  href={appStoreLink}
                  className="banner-content__buttons-item"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => {
                    if (appStoreLink === "#") {
                      e.preventDefault();
                    }
                  }}
                >
                  <Image
                    src="https://res.cloudinary.com/dpkn1ppzj/image/upload/v1752696902/Group_239851_n7jq84.png"
                    alt="App Store"
                    width={142}
                    height={42}
                  />
                </Link>
              </motion.div>
            </div>
          </motion.div>

          {/* Fixed: Remove the dot from the fallback class name */}
          <div className={`${countryImages?.fiting || "banner-country-UK"}`}>
            <AnimatePresence mode="wait">
              <motion.img
                key={imageKey}
                src={currentImage}
                alt="Country"
                className={`banner-country-image ${isTransitioning ? "slide-in" : ""} `}
                style={{
                  width: countryImages?.width
                    ? `${countryImages.width}px`
                    : "674px",
                  height: countryImages?.height
                    ? `${countryImages.height}px`
                    : "1012px",
                }}
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -40 }}
                transition={{ duration: 0.6 }}
              />
            </AnimatePresence>
          </div>
        </div>

        {/* Render Convert here so country selection is inside the banner */}
        <div style={{ width: "100%", overflow: "visible" }}>
          <Suspense fallback={<ConvertSkeleton />}>
            <Convert />
          </Suspense>
        </div>

        <motion.div
          style={{
            zIndex: "2",
            position: "absolute",
            bottom: "10%",
            left: "0",
          }}
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 1.2 }}
        >
          <Image
            src={
              countryImages?.countryFlag ||
              "https://res.cloudinary.com/dpkn1ppzj/image/upload/v1754986445/emojione_flag-for-united-kingdom_bcbadx.png"
            }
            alt="Country Flag"
            width={356}
            height={356}
          />
        </motion.div>
      </div>
    </section>
  );
};
